import { LAST_USED_MODEL_OR_ASSISTANT_UUID_STORAGE_KEY } from "@frontend-shared/constants";
import type {
  IModelSettings,
  IModelSettingsPayload,
  IModelSettingsResponse,
  TWritableModelSetting,
} from "@frontend-shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { modelsApi } from "@/api/services";
import { ModelSettingsContext } from "@/contexts/ModelSettingsContext";
import { useAuth } from "@/hooks/use-auth";

export const MODEL_SETTINGS_QUERY_KEY = "model-settings";

// NOTE: NSAI-4838 — maps the local IModelSettings keys for the three persisted
// defaults to their server payload counterparts. `lastUsedModelOrAssistant` is
// absent on purpose — it never reaches the server.
const SERVER_KEY_BY_SETTING = {
  defaultModel: "defaultModelUuid",
  defaultSummaryModel: "defaultSummaryModelUuid",
  defaultImageModel: "defaultImageModelUuid",
} as const satisfies Record<
  Exclude<TWritableModelSetting, "lastUsedModelOrAssistant">,
  keyof IModelSettingsPayload
>;

const readLastUsed = (): string | null =>
  localStorage.getItem(LAST_USED_MODEL_OR_ASSISTANT_UUID_STORAGE_KEY);

interface IModelSettingsProviderProps {
  children: React.ReactNode;
}

const ModelSettingsProviderComponent = ({
  children,
}: IModelSettingsProviderProps): React.ReactElement => {
  const queryClient = useQueryClient();
  const { currentProjectUuid } = useAuth();

  // NOTE: last-used-model is the only device-local setting; keep it in state so
  // reads are reactive, seeded from (and mirrored to) localStorage.
  const [lastUsedModelOrAssistant, setLastUsedModelOrAssistant] = useState<
    string | null
  >(() => readLastUsed());

  const { data: serverSettings, isLoading: isModelSettingsLoading } = useQuery({
    // NOTE: keyed by project — the defaults are per-project, so switching
    // projects refetches instead of serving another project's defaults.
    queryKey: [MODEL_SETTINGS_QUERY_KEY, currentProjectUuid],
    queryFn: () => modelsApi.getModelSettings(),
    enabled: !!currentProjectUuid,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<IModelSettingsPayload>) =>
      modelsApi.updateModelSettings(data),
    // NOTE: define the callbacks on the mutation (not per-call) so concurrent
    // updates to different defaults can't drop each other's settle handler.
    onSuccess: (result) => {
      queryClient.setQueryData(
        [MODEL_SETTINGS_QUERY_KEY, currentProjectUuid],
        result,
      );
    },
    onError: () => {
      // NOTE: a failed write leaves an optimistic value in the cache; refetch the
      // authoritative server state to correct it.
      queryClient.invalidateQueries({
        queryKey: [MODEL_SETTINGS_QUERY_KEY, currentProjectUuid],
      });
    },
  });

  const setModelSettings = useCallback(
    async (
      key: TWritableModelSetting,
      value: string | null,
    ): Promise<boolean> => {
      if (key === "lastUsedModelOrAssistant") {
        setLastUsedModelOrAssistant(value);
        if (value) {
          localStorage.setItem(
            LAST_USED_MODEL_OR_ASSISTANT_UUID_STORAGE_KEY,
            value,
          );
        } else {
          localStorage.removeItem(
            LAST_USED_MODEL_OR_ASSISTANT_UUID_STORAGE_KEY,
          );
        }
        return true;
      }

      const serverKey = SERVER_KEY_BY_SETTING[key];
      // NOTE: optimistic cache write so the selector moves immediately; the
      // mutation's onSuccess reconciles with the persisted server state.
      queryClient.setQueryData<IModelSettingsResponse>(
        [MODEL_SETTINGS_QUERY_KEY, currentProjectUuid],
        (previous) => ({
          defaultModelUuid: previous?.defaultModelUuid ?? null,
          defaultSummaryModelUuid: previous?.defaultSummaryModelUuid ?? null,
          defaultImageModelUuid: previous?.defaultImageModelUuid ?? null,
          // NOTE: NSAI-5786 — carried through untouched. It is server-owned
          // deployment config, so an optimistic write of a user default must
          // not blank it out of the cache until the refetch restores it.
          forcedNewChatModelName: previous?.forcedNewChatModelName ?? null,
          // NOTE: NSAI-6445 — server-resolved like the field above, so it is
          // carried through the same way.
          summaryFallbackModel: previous?.summaryFallbackModel ?? null,
          [serverKey]: value,
        }),
      );
      // NOTE: NSAI-4838 (review) — await the write and report success so callers
      // can order dependent work AFTER it lands. Never rejects: onError already
      // rolls the optimistic value back via invalidation, and returning false
      // keeps fire-and-forget callers safe.
      try {
        await updateMutation.mutateAsync({ [serverKey]: value });
        return true;
      } catch {
        return false;
      }
    },
    [queryClient, currentProjectUuid, updateMutation],
  );

  // NOTE: keep last-used in sync across tabs (other settings sync via the query
  // cache / server, not localStorage).
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent): void => {
      if (event.key === LAST_USED_MODEL_OR_ASSISTANT_UUID_STORAGE_KEY) {
        setLastUsedModelOrAssistant(event.newValue);
      }
    };

    globalThis.addEventListener("storage", handleStorageChange);
    return () => globalThis.removeEventListener("storage", handleStorageChange);
  }, []);

  const modelSettings = useMemo<IModelSettings>(
    () => ({
      defaultModel: serverSettings?.defaultModelUuid ?? null,
      defaultSummaryModel: serverSettings?.defaultSummaryModelUuid ?? null,
      defaultImageModel: serverSettings?.defaultImageModelUuid ?? null,
      lastUsedModelOrAssistant,
      forcedNewChatModelName: serverSettings?.forcedNewChatModelName ?? null,
      summaryFallbackModel: serverSettings?.summaryFallbackModel ?? null,
    }),
    [serverSettings, lastUsedModelOrAssistant],
  );

  const value = useMemo(
    () => ({ modelSettings, setModelSettings, isModelSettingsLoading }),
    [modelSettings, setModelSettings, isModelSettingsLoading],
  );

  return (
    <ModelSettingsContext.Provider value={value}>
      {children}
    </ModelSettingsContext.Provider>
  );
};

export const ModelSettingsProvider = memo(ModelSettingsProviderComponent);
